/**
 * StackContext - Named stack state management
 *
 * Manages the list of named stacks for the active project.
 * Wraps inside ProjectProvider. When activeProjectId changes, refreshes stacks.
 *
 * BufferSyncContext reads `activeStackId` from here to persist
 * card stack operations to the correct named stack.
 *
 * @module @humanizer/studio/contexts/StackContext
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useProject } from './ProjectContext';
import type { ProjectStackData, StackBufferData } from '../types/electron';
import { createLogger } from '../utils/debug';

const log = createLogger('StackContext');

interface StackContextValue {
  stacks: ProjectStackData[];
  activeStack: ProjectStackData | null;
  activeStackId: string | null;
  setActiveStack: (id: string | null) => void;
  createStack: (name: string, description?: string) => Promise<ProjectStackData | null>;
  updateStack: (id: string, update: { name?: string; description?: string }) => Promise<void>;
  deleteStack: (id: string) => Promise<void>;
  refreshStacks: () => Promise<void>;

  /** Create a "Working Stack" and set it active. Returns the new stack. */
  ensureWorkingStack: () => Promise<ProjectStackData | null>;
}

const StackContext = createContext<StackContextValue | null>(null);

export function useStacks(): StackContextValue {
  const ctx = useContext(StackContext);
  if (!ctx) throw new Error('useStacks must be used within StackProvider');
  return ctx;
}

export function StackProvider({ children }: { children: React.ReactNode }) {
  const { activeProjectId } = useProject();
  const [stacks, setStacks] = useState<ProjectStackData[]>([]);
  const [activeStackId, setActiveStackId] = useState<string | null>(null);
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;
  const prevProjectRef = useRef<string | null>(null);

  // Derived
  const activeStack = stacks.find(s => s.id === activeStackId) ?? null;

  // ─────────────────────────────────────────────────────────────────
  // REFRESH
  // ─────────────────────────────────────────────────────────────────

  const refreshStacks = useCallback(async () => {
    if (!isElectron || !activeProjectId || !window.electronStack) {
      setStacks([]);
      return;
    }
    try {
      const result = await window.electronStack.list(activeProjectId);
      setStacks(result ?? []);
    } catch (err) {
      log.warn('Failed to refresh stacks:', err);
      setStacks([]);
    }
  }, [isElectron, activeProjectId]);

  // Refresh when project changes
  useEffect(() => {
    if (activeProjectId !== prevProjectRef.current) {
      prevProjectRef.current = activeProjectId;
      setActiveStackId(null);
      refreshStacks().then(() => {
        // Restore persisted activeStackId from electron-store
        if (isElectron && activeProjectId && window.electronAPI?.store) {
          window.electronAPI.store.get(`activeStackId.${activeProjectId}`).then((id) => {
            if (typeof id === 'string') setActiveStackId(id);
          }).catch(() => {});
        }
      });
    }
  }, [activeProjectId, refreshStacks, isElectron]);

  // Persist activeStackId to electron-store
  useEffect(() => {
    if (!isElectron || !activeProjectId || !window.electronAPI?.store) return;
    if (activeStackId) {
      window.electronAPI.store.set(`activeStackId.${activeProjectId}`, activeStackId).catch(() => {});
    } else {
      window.electronAPI.store.delete(`activeStackId.${activeProjectId}`).catch(() => {});
    }
  }, [isElectron, activeProjectId, activeStackId]);

  // ─────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────

  const createStack = useCallback(async (name: string, description?: string): Promise<ProjectStackData | null> => {
    if (!isElectron || !activeProjectId || !window.electronStack) return null;
    try {
      const stack = await window.electronStack.create(activeProjectId, name, description);
      setStacks(prev => [stack, ...prev]);
      return stack;
    } catch (err) {
      log.warn('Create failed:', err);
      return null;
    }
  }, [isElectron, activeProjectId]);

  const updateStack = useCallback(async (id: string, update: { name?: string; description?: string }) => {
    if (!isElectron || !window.electronStack) return;
    try {
      const updated = await window.electronStack.update(id, update);
      if (updated) {
        setStacks(prev => prev.map(s => s.id === id ? updated : s));
      }
    } catch (err) {
      log.warn('Update failed:', err);
    }
  }, [isElectron]);

  const deleteStack = useCallback(async (id: string) => {
    if (!isElectron || !window.electronStack) return;
    try {
      await window.electronStack.delete(id);
      setStacks(prev => prev.filter(s => s.id !== id));
      if (activeStackId === id) setActiveStackId(null);
    } catch (err) {
      log.warn('Delete failed:', err);
    }
  }, [isElectron, activeStackId]);

  const setActiveStack = useCallback((id: string | null) => {
    setActiveStackId(id);
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // AUTO-CREATE "Working Stack"
  // ─────────────────────────────────────────────────────────────────

  const ensureWorkingStack = useCallback(async (): Promise<ProjectStackData | null> => {
    // If already have an active stack, return it
    if (activeStackId) {
      const existing = stacks.find(s => s.id === activeStackId);
      if (existing) return existing;
    }

    // If project has stacks, activate the first one
    if (stacks.length > 0) {
      setActiveStackId(stacks[0].id);
      return stacks[0];
    }

    // Create "Working Stack"
    const stack = await createStack('Working Stack');
    if (stack) {
      setActiveStackId(stack.id);
    }
    return stack;
  }, [activeStackId, stacks, createStack]);

  const value: StackContextValue = {
    stacks,
    activeStack,
    activeStackId,
    setActiveStack,
    createStack,
    updateStack,
    deleteStack,
    refreshStacks,
    ensureWorkingStack,
  };

  return (
    <StackContext.Provider value={value}>
      {children}
    </StackContext.Provider>
  );
}
